import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Illustration, Textblock, Typography, useModalPortal, useModalState } from '@screentone/core';
import { isSameDay } from 'date-fns';

import { ResponsiveLoader } from 'components';
import { useConvertedProperty } from 'hooks';
import { useIsAdmin, useIsDebug, useIsSuper } from 'hooks/roles';
import { PUBLISH_HISTORY_PREFETCH_THRESHOLD } from 'utils/constants';
import { reportError } from 'utils/errorBoundary';
import { CompareRevisionsModal } from './CompareRevisionsModal';
import { HistoryItem } from './HistoryItem';
import { useGapBoundedNextIndex } from './hooks/useGapBoundedNextIndex';
import { usePublishHistoryItems } from './hooks/usePublishHistoryItems';
import { usePublishHistoryNavigation } from './hooks/usePublishHistoryNavigation';
import { usePublishHistoryQueries } from './hooks/usePublishHistoryQueries';
import { useTimestampFormatters } from './hooks/useTimestampFormatters';
import { useWindowSnap } from './hooks/useWindowSnap';
import styles from './PublishHistory.module.scss';
import { PublishHistoryToolbar } from './PublishHistoryToolbar';
import { HistoryContent } from './types';

export type { HistoryContent };

interface PageHistorySectionProps<T extends HistoryContent> {
  liveVersion: T | undefined;
  publishUtc: number;
  hasRecentlyPublished: boolean;
  originalVersionWhenInHistoryEditMode?: T | null;
  currentlyEditedVersion?: T;
  handleShowHistoryEditMode?: (historyItem: HistoryContent) => void;
  scrollableSectionClassName?: string;
  handlePreviewClick?: (selectedVersion: T) => void;
  isPreviewLoading?: boolean;
  // showURL?: boolean;
  isReadOnly?: boolean;
  hidePreview?: boolean;
  initialPageSize?: number;
}

export const PublishHistory = <T extends HistoryContent>({
  liveVersion,
  publishUtc,
  hasRecentlyPublished,
  originalVersionWhenInHistoryEditMode,
  currentlyEditedVersion,
  handleShowHistoryEditMode,
  scrollableSectionClassName,
  handlePreviewClick,
  isPreviewLoading,
  isReadOnly,
  hidePreview = false,
  initialPageSize
}: PageHistorySectionProps<T>) => {
  const [selectedDate, setSelectedDate] = useState<number | null>(Date.now());
  const [windowOlderAnchor, setWindowOlderAnchor] = useState<number | null>(null);
  const [windowNewerAnchor, setWindowNewerAnchor] = useState<number | null>(null);
  const [windowTarget, setWindowTarget] = useState<number | null>(null);
  const [windowItems, setWindowItems] = useState<T[]>([]);
  const currentProperty = useConvertedProperty();
  const { open, openModal, closeModal } = useModalState();
  const { renderNode } = useModalPortal();
  const isDebug = useIsDebug();
  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const isAllowedToCompare = isAdmin || isSuper || isDebug;
  const { isETTimestamp, formatTimestamp, formatTimestampET } = useTimestampFormatters();

  const {
    scheduledContents,
    isLoadingScheduledContent,
    refetchScheduledContent,
    scheduledContentFetchStatus,
    isFetchingScheduledContent,
    lastNScheduledContentsData,
    fetchNextPublishedPage,
    hasNextPublishedPage,
    isLoadingLastNScheduledContent,
    lastNScheduledContentFetchStatus,
    isFetchingLastNScheduledContent,
    lastNFromWindowData,
    fetchOlderFromWindow,
    hasOlderFromWindow,
    isFetchingOlderFromWindow,
    nextNFromWindowData,
    fetchNewerFromWindow,
    hasNewerFromWindow,
    isFetchingNewerFromWindow,
    windowData,
    isFetchingWindow,
    publishedTimestamps
  } = usePublishHistoryQueries({
    liveVersion,
    currentProperty,
    windowTarget,
    windowOlderAnchor,
    windowNewerAnchor,
    initialPageSize
  });

  const isLoading =
    (isLoadingScheduledContent && scheduledContentFetchStatus !== 'idle') ||
    (isLoadingLastNScheduledContent && lastNScheduledContentFetchStatus !== 'idle');

  const history = usePublishHistoryItems({
    liveVersion,
    scheduledContents,
    lastNScheduledContentsData,
    lastNFromWindowData,
    nextNFromWindowData,
    windowItems
  });

  const { setCurrentItemPublishUtc, setUserPaginated, currentIndex } = usePublishHistoryNavigation({
    history,
    liveVersion,
    publishUtc,
    originalVersionWhenInHistoryEditMode,
    currentlyEditedVersion
  });

  const handleReset = () => {
    setUserPaginated(false);
    setWindowItems([]);
    setWindowOlderAnchor(null);
    setWindowNewerAnchor(null);
    setWindowTarget(null);
    setCurrentItemPublishUtc(liveVersion?.metadata.publishUtc ?? null);
    setSelectedDate(Date.now());
  };

  useEffect(() => {
    async function reset() {
      if (hasRecentlyPublished) {
        handleReset();
        await refetchScheduledContent();
      }
    }
    void reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRecentlyPublished, refetchScheduledContent]);

  useWindowSnap({
    liveVersion,
    windowData,
    windowTarget,
    setWindowTarget,
    setWindowItems,
    setWindowOlderAnchor,
    setWindowNewerAnchor,
    setCurrentItemPublishUtc
  });

  const revisions = history;

  const effectiveNextEndIndex = useGapBoundedNextIndex({
    revisions,
    hasNewerFromWindow,
    liveVersion,
    windowItems,
    nextNFromWindowData
  });

  const handleClickPrev = () => {
    if (currentIndex <= PUBLISH_HISTORY_PREFETCH_THRESHOLD) {
      const oldestLoadedUtc = revisions[0]?.metadata.publishUtc;
      const usingWindowOlder =
        windowOlderAnchor != null && oldestLoadedUtc != null && oldestLoadedUtc <= windowOlderAnchor;
      if (usingWindowOlder) {
        if (hasOlderFromWindow && !isFetchingOlderFromWindow) void fetchOlderFromWindow();
      } else if (hasNextPublishedPage && !isFetchingLastNScheduledContent) {
        void fetchNextPublishedPage();
      }
    }
    if (currentIndex === 0) return;
    setUserPaginated(true);
    const newItem = revisions[currentIndex - 1];
    setCurrentItemPublishUtc(newItem.metadata.publishUtc ?? null);
  };

  const handleClickNext = () => {
    const distanceToEnd = effectiveNextEndIndex - currentIndex;
    if (distanceToEnd <= PUBLISH_HISTORY_PREFETCH_THRESHOLD) {
      if (hasNewerFromWindow && !isFetchingNewerFromWindow) {
        void fetchNewerFromWindow();
      }
    }
    if (currentIndex >= effectiveNextEndIndex) return;

    setUserPaginated(true);
    const newItem = revisions[currentIndex + 1];
    setCurrentItemPublishUtc(newItem.metadata.publishUtc ?? null);
  };

  const handleSelectDate = (dateNum: number) => {
    setSelectedDate(dateNum);
  };

  const handleSelectRevision = (targetPublishUtc?: number) => {
    if (!history.length || !targetPublishUtc) return;

    const isLoaded = history.some((item) => item.metadata.publishUtc === targetPublishUtc);

    setUserPaginated(true);

    if (isLoaded) {
      setCurrentItemPublishUtc(targetPublishUtc);
    } else {
      setWindowOlderAnchor(null);
      setWindowNewerAnchor(null);
      setWindowTarget(targetPublishUtc);
    }
  };

  if (isLoading) {
    return (
      <div>
        <Typography margin={{ bottom: 'md' }} variant="header3">
          Publish History
        </Typography>
        <ResponsiveLoader />
      </div>
    );
  }

  if (!history.length)
    return (
      <>
        <Typography variant="header3" margin={{ bottom: 'md' }}>
          Publish History
        </Typography>
        <Illustration name="no-data" className={styles.illustration} />
        <Typography componentEl="p" size="sm" align="center">
          This content has never been published!
        </Typography>
      </>
    );

  const currentItem = revisions[currentIndex];

  const hasNext = currentIndex < effectiveNextEndIndex || !!hasNewerFromWindow;
  const isWindowMode = windowOlderAnchor != null;
  const hasPrev = currentIndex > 0 || !!hasOlderFromWindow || (!isWindowMode && !!hasNextPublishedPage);
  const disabledNavigationInEditMode = !!originalVersionWhenInHistoryEditMode;
  const isAtPrevBoundary = currentIndex === 0 && (isFetchingLastNScheduledContent || isFetchingOlderFromWindow);
  const isAtNextBoundary = currentIndex >= effectiveNextEndIndex && isFetchingNewerFromWindow;
  const dateGroupsForCalendarPublished = publishedTimestamps
    .filter((t) => isSameDay(t.publishUtc, selectedDate!) && t.publishUtc < liveVersion!.metadata.publishUtc!)
    .map((t) => t.publishUtc);
  const dateGroupsForCalendarScheduled = history
    .filter(
      (item) =>
        isSameDay(item.metadata.publishUtc!, selectedDate!) &&
        item.metadata.publishUtc! > liveVersion!.metadata.publishUtc!
    )
    .map((item) => item.metadata.publishUtc!);

  const prevRevision: typeof currentItem | undefined = revisions[currentIndex - 1];

  return (
    <div>
      <Typography margin={{ bottom: 'md' }} variant="header3" data-testid="page-publish-history-title">
        Publish History
      </Typography>
      <PublishHistoryToolbar
        onPrev={handleClickPrev}
        onNext={handleClickNext}
        hasPrev={hasPrev}
        hasNext={hasNext}
        isAtPrevBoundary={isAtPrevBoundary}
        isAtNextBoundary={isAtNextBoundary}
        disabledNavigationInEditMode={disabledNavigationInEditMode}
        isFetchingScheduledContent={isFetchingScheduledContent}
        isAllowedToCompare={isAllowedToCompare}
        onOpenCompare={openModal}
        selectedDate={selectedDate}
        onSelectRevision={handleSelectRevision}
        onSelectDate={handleSelectDate}
        onReset={handleReset}
        dateGroupsScheduled={dateGroupsForCalendarScheduled}
        dateGroupsPublished={dateGroupsForCalendarPublished}
        areRevisionsLoading={isFetchingWindow}
      />
      <ErrorBoundary
        key={currentItem.metadata.publishUtc}
        onError={(err) => reportError('Error rendering publish history version:', err)}
        fallbackRender={({ error }) => (
          <Textblock margin={{ top: 'md' }}>
            <p>This version could not be displayed.</p>
            <p><code>{(error as Error).message}</code></p>
          </Textblock>
        )}
      >
        <HistoryItem
          selectedVersion={currentItem}
          isLive={currentItem.metadata.publishUtc === liveVersion?.metadata.publishUtc}
          isPast={currentItem.metadata.publishUtc! < (liveVersion?.metadata.publishUtc ?? 0)}
          isEditing={!!currentlyEditedVersion}
          handleShowHistoryEditMode={handleShowHistoryEditMode}
          scrollableSectionClassName={scrollableSectionClassName}
          handlePreviewClick={handlePreviewClick}
          isPreviewLoading={isPreviewLoading}
          isWindowLoading={isFetchingWindow}
          isReadOnly={isReadOnly}
          hidePreview={hidePreview}
        // showURL={showURL}
        />
      </ErrorBoundary>
      <CompareRevisionsModal
        isOpen={open}
        onDismiss={closeModal}
        renderNode={renderNode}
        currentItem={currentItem}
        prevRevision={prevRevision}
        isETTimestamp={isETTimestamp}
        formatTimestamp={formatTimestamp}
        formatTimestampET={formatTimestampET}
      />
    </div>
  );
};
